import { ScreeningReportType, ScreeningRestrictionItem } from '@/app/common/general-types/social-screening.dto';
import { CUSTOM_RESTRICTION_CATEGORY_ID } from './screening-brief.constants';

/** Collapse HTML to its text content. */
export function stripTags(value: string): string {
  return value.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
}

/** Escape user text before it is embedded in the HTML we store on the brief. */
export function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Parse an HTML `<li>` list (or plain HTML) into editable lines. */
export function htmlListToLines(html: string): string[] {
  if (!html) {
    return [];
  }
  const listItems = [...html.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)]
    .map((match) => stripTags(match[1]))
    .filter((line) => line.length > 0);
  if (listItems.length) {
    return listItems;
  }
  const text = stripTags(html);
  return text ? [text] : [];
}

/** Serialise lines back to the `<ul><li>` HTML the manage-brief screen renders. */
export function linesToHtmlList(items: string[]): string {
  const clean = items.filter((item) => item.trim().length > 0);
  if (!clean.length) {
    return '';
  }
  return `<ul>${clean.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>`;
}

/** Wrap free prose as paragraph HTML, preserving the user's line breaks. */
export function proseToHtml(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) {
    return '';
  }
  return `<p>${escapeHtml(trimmed).replace(/\r?\n/g, '<br>')}</p>`;
}

/** A restriction is unique on its category plus its text. */
export function restrictionKey(categoryId: string, text: string): string {
  return `${categoryId}::${text.trim()}`;
}

/** The snapshot the pre-flight check reads. Kept flat so the evaluator stays pure. */
export interface ScreeningBriefSnapshot {
  lookFor: string;
  restrictions: ScreeningRestrictionItem[];
  reportType: ScreeningReportType | undefined;
  reportPrompt: string;
}

export interface ScreeningPreflightResult {
  pass: boolean;
  reasons: string[];
}

/** Minimum characters before the requirements text reads as a real brief. */
const LOOKFOR_DEPTH_CHARS = 40;
/** Minimum characters for a usable custom-analysis spec. */
const REPORT_PROMPT_MIN_CHARS = 20;

/**
 * Judges whether the brief has enough depth to screen against.
 *
 * Pure by design: it is the single seam where a real
 * `POST /social-screening/validate-brief` would replace the local heuristic, and it unit-tests
 * without a TestBed.
 */
export function evaluateScreeningBrief(snapshot: ScreeningBriefSnapshot): ScreeningPreflightResult {
  const reasons: string[] = [];

  if ((snapshot.lookFor || '').trim().length < LOOKFOR_DEPTH_CHARS) {
    reasons.push('Your “what to look for” is too short — add more detail so the AI knows what matters.');
  }
  if (snapshot.reportType === 'custom' && (snapshot.reportPrompt || '').trim().length < REPORT_PROMPT_MIN_CHARS) {
    reasons.push("You chose a custom analysis but haven't described what you want in the report.");
  }
  if (!snapshot.reportType) {
    reasons.push('No reporting type chosen — pick the standard report or describe a custom analysis.');
  }
  if (snapshot.restrictions.length < 1) {
    reasons.push('No red flags defined — add at least one restriction the AI should catch.');
  }
  if (snapshot.restrictions.length === 1 && snapshot.restrictions[0].custom) {
    reasons.push('Only one restriction set — a thin brief misses more than it catches. Add a few more.');
  }

  return { pass: reasons.length === 0, reasons };
}

/** Group restrictions for the review card: `Category · count`. */
export function groupRestrictionsByCategory(
  restrictions: ScreeningRestrictionItem[],
): { label: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const restriction of restrictions) {
    const label = restriction.custom ? CUSTOM_RESTRICTION_CATEGORY_ID : restriction.categoryLabel;
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  return [...counts.entries()].map(([label, count]) => ({ label, count }));
}
